import ApiClient from './ApiClient';

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;

// Same wire-casing situation as StripeClient: the mollie/status and mollie/connect DTOs serialize
// as the exact C# property names (PascalCase: Connected, AuthorizeUrl), while mollie/disconnect's
// response is already camelCase (the handler writes it as an anonymous-object literal). Normalize
// at the client boundary so the rest of the app never has to know about the split.
const toCamelKey = key => key.charAt(0).toLowerCase() + key.slice(1);

const toCamelCaseShallow = obj => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [toCamelKey(key), value])
  );
};

class MollieClient extends ApiClient {
  constructor(url) {
    super(url ? `${url}/mollie` : '/mollie');
  }

  // GET mollie/status — admin-only read of the caller's own org's Mollie connection status.
  // Returns { connected }.
  async getStatus() {
    try {
      const data = await this.get('/status');
      return toCamelCaseShallow(data);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch Mollie status.')
      );
    }
  }

  // POST mollie/connect — admin-only; returns { authorizeUrl } to redirect the browser to.
  async connect() {
    try {
      const data = await this.post('/connect');
      return toCamelCaseShallow(data);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to initiate Mollie Connect.')
      );
    }
  }

  // POST mollie/disconnect — admin-only; removes the org's stored Mollie token.
  async disconnect() {
    try {
      return await this.post('/disconnect');
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to disconnect Mollie.')
      );
    }
  }
}

export default MollieClient;
